import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, NotePencil, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = ["general", "operations", "client", "issue", "reminder"];
const empty = { title: "", content: "", category: "general", shared: false };

export default function Notes() {
  const { user } = useAuth();
  const invalidate = useInvalidate();
  const { data: notes = [], isLoading } = useList("notes", "/notes");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (n) => { setForm({ title: n.title, content: n.content, category: n.category, shared: n.shared }); setEditing(n.id); setOpen(true); };
  const save = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    setSaving(true);
    try { if (editing) await api.put(`/notes/${editing}`, form); else await api.post("/notes", form); toast.success("Saved"); setOpen(false); invalidate("notes"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete note?")) return; await api.delete(`/notes/${id}`); invalidate("notes"); };

  const palette = { general: "border-l-slate-500", operations: "border-l-blue-500", client: "border-l-green-500", issue: "border-l-red-500", reminder: "border-l-blue-600" };

  return (
    <div>
      <PageHeader title="Working Notes" subtitle="Your private notes. Only you can see them."
        actions={<Button data-testid="new-note-btn" onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />New Note</Button>} />

      {isLoading ? <div className="text-slate-500">Loading…</div> : notes.length === 0 ? (
        <Panel><EmptyState icon={NotePencil} title="No notes yet" hint="Jot down operational notes, client details or reminders." /></Panel>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {notes.map((n) => (
            <Panel key={n.id} className={`p-4 border-l-2 ${palette[n.category] || "border-l-slate-500"}`} data-testid={`note-${n.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{n.title}</p>
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 capitalize">{n.category}</span>
                </div>
                <div className="flex gap-1">
                  {(user.role === "admin" || n.created_by === user.id) && <button onClick={() => openEdit(n)} className="p-1 text-slate-500 hover:text-blue-600"><PencilSimple size={15} /></button>}
                  {(user.role === "admin" || n.created_by === user.id) && <button onClick={() => remove(n.id)} className="p-1 text-slate-500 hover:text-red-400"><Trash size={15} /></button>}
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-2 whitespace-pre-wrap line-clamp-6">{n.content}</p>
              <p className="text-[11px] text-slate-400 mt-3">{n.author_name} · {new Date(n.updated_at).toLocaleDateString()}</p>
            </Panel>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Note" : "New Note"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Title *"><Input data-testid="note-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white border-slate-300" /></Field>
            <Field label="Content"><Textarea data-testid="note-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="bg-white border-slate-300" rows={5} /></Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-white border-slate-300 w-44" data-testid="note-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button data-testid="save-note-btn" onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">{saving ? "Saving…" : "Save Note"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
